import { Injectable, OnDestroy, inject } from '@angular/core';
import { FormBuilder, ValidatorFn, Validators } from '@angular/forms';

import { SettingsRequestStateBase } from './settings-request-state.base';
import { CompanyOperationsSettingsDto } from '../data-access/settings.models';
import { SettingsService } from '../data-access/settings.service';
import { buildTimeZoneCatalog, findTimeZoneRegion } from './settings-time-zone.util';

const publicAppUrlValidator: ValidatorFn = (control) => {
  const value = typeof control.value === 'string' ? control.value.trim() : '';
  if (!value) return null;
  try {
    const url = new URL(value);
    return /^https?:\/\//i.test(value) && ['http:', 'https:'].includes(url.protocol) && url.hostname && !url.username && !url.password
      ? null : { publicAppUrl: true };
  } catch {
    return { publicAppUrl: true };
  }
};

@Injectable()
export class OperationsSettingsStateService extends SettingsRequestStateBase implements OnDestroy
{
  private loadedCompanyId: string | undefined;
  private initialPublicAppBaseUrl: string | null = null;
  public inheritedPublicAppBaseUrl: string | null = null;
  public static readonly DEFAULT_CLINIC_LABEL = 'Clinic';
  public static readonly DEFAULT_PROCEDURE_TYPE_LABEL = 'Procedure';

  private readonly formBuilder = inject(FormBuilder);
  private readonly settingsService = inject(SettingsService);
  private readonly timeZoneCatalog = buildTimeZoneCatalog();

  public readonly timeZoneRegions = Array.from(this.timeZoneCatalog.keys());
  public selectedTimeZoneRegion = findTimeZoneRegion(this.timeZoneCatalog, 'UTC');
  public sourceText: CompanyOperationsSettingsDto['source'] | null = null;
  public publicAppBaseUrlSource: CompanyOperationsSettingsDto['publicAppBaseUrlSource'] | null = null;
  public pendingSlotDurationMinutes: number | null = null;
  public pendingSlotDurationEffectiveFromDate: string | null = null;
  public initialSlotDurationMinutes: number | null = null;

  public readonly form = this.formBuilder.group({
    useGlobalPublicAppBaseUrl: this.formBuilder.nonNullable.control(false),
    cancelPendingSlotDurationChange: this.formBuilder.nonNullable.control(false),
    slotDurationMinutes: this.formBuilder.nonNullable.control(15, [
      Validators.required,
      Validators.min(5),
      Validators.max(180),
    ]),
    generateDaysAhead: this.formBuilder.nonNullable.control(14, [
      Validators.required,
      Validators.min(1),
      Validators.max(90),
    ]),
    schedulerIntervalMinutes: this.formBuilder.nonNullable.control(60, [
      Validators.required,
      Validators.min(5),
      Validators.max(1440),
    ]),
    schedulerEnabled: this.formBuilder.nonNullable.control(false),
    appointmentCompletionMode: this.formBuilder.nonNullable.control<'auto' | 'manual_with_checkin'>('auto'),
    timeZone: this.formBuilder.nonNullable.control('UTC', [
      Validators.required,
      Validators.maxLength(128),
    ]),
    clinicLabel: this.formBuilder.nonNullable.control(OperationsSettingsStateService.DEFAULT_CLINIC_LABEL, [
      Validators.required,
      Validators.maxLength(64),
    ]),
    procedureTypeLabel: this.formBuilder.nonNullable.control(
      OperationsSettingsStateService.DEFAULT_PROCEDURE_TYPE_LABEL,
      [
        Validators.required,
        Validators.maxLength(64),
      ],
    ),
    publicAppBaseUrl: this.formBuilder.nonNullable.control('', [publicAppUrlValidator]),
  });

  public get usesInheritedPublicAppBaseUrl(): boolean {
    return Boolean(this.loadedCompanyId && this.form.controls.useGlobalPublicAppBaseUrl.value);
  }

  public get currentSlotDurationMinutes(): number
  {
    return this.form.controls.slotDurationMinutes.value;
  }

  public get saving(): boolean
  {
    return this.isSaving;
  }

  public get error(): string | null
  {
    return this.errorText;
  }

  public get success(): string | null
  {
    return this.successText;
  }

  public get isSlotDurationChanged(): boolean
  {
    return this.initialSlotDurationMinutes !== null
      && this.currentSlotDurationMinutes !== this.initialSlotDurationMinutes;
  }

  public get timeZoneOptions() {
    return this.timeZoneCatalog.get(this.selectedTimeZoneRegion) ?? [];
  }

  public getPublicAppBaseUrlSourceText(companyId?: string): string | null {
    if (this.publicAppBaseUrlSource === 'company_override') {
      return companyId ? 'company override' : 'global default';
    }

    if (this.publicAppBaseUrlSource === 'global_default') {
      return 'inherited from global settings';
    }

    if (this.publicAppBaseUrlSource === 'unset') {
      return 'not configured';
    }

    return null;
  }

  public get pendingSlotDurationBannerText(): string | null {
    if (!this.pendingSlotDurationMinutes || !this.pendingSlotDurationEffectiveFromDate) {
      return null;
    }

    return `Scheduled slot duration change: ${this.pendingSlotDurationMinutes} min from ${this.pendingSlotDurationEffectiveFromDate}. Already published slots remain unchanged.`;
  }

  public load(companyId?: string): void
  {
    this.loadedCompanyId = companyId;
    this.sourceText = null;
    this.publicAppBaseUrlSource = null;
    this.initialPublicAppBaseUrl = null;
    this.inheritedPublicAppBaseUrl = null;
    this.pendingSlotDurationMinutes = null;
    this.pendingSlotDurationEffectiveFromDate = null;
    this.initialSlotDurationMinutes = null;
    this.selectedTimeZoneRegion = findTimeZoneRegion(this.timeZoneCatalog, 'UTC');

    this.loadSettings(this.settingsService.getOperationsSettings(companyId), this.form).subscribe({
      next: (settings: CompanyOperationsSettingsDto) =>
      {
        this.applySettings(settings);
      },
      error: (error: unknown) =>
      {
        this.setHttpError(error);
      },
    });
  }

  public save(companyId?: string): void
  {
    if (this.form.invalid || !this.canSaveSettings || companyId !== this.loadedCompanyId)
    {
      return;
    }

    this.beginSaving();

    const value = this.form.getRawValue();

    this.currentSettingsRequest(this.settingsService.updateOperationsSettings({
      slotDurationMinutes: value.slotDurationMinutes !== this.initialSlotDurationMinutes
        ? value.slotDurationMinutes : undefined,
      cancelPendingSlotDurationChange: value.cancelPendingSlotDurationChange || undefined,
      appointmentCompletionMode: value.appointmentCompletionMode,
      generateDaysAhead: value.generateDaysAhead,
      schedulerIntervalMinutes: value.schedulerIntervalMinutes,
      schedulerEnabled: value.schedulerEnabled,
      timeZone: value.timeZone.trim(),
      clinicLabel: value.clinicLabel,
      procedureTypeLabel: value.procedureTypeLabel,
      publicAppBaseUrl: this.publicAppBaseUrlPatch(),
    }, companyId)).subscribe({
      next: (response: CompanyOperationsSettingsDto) =>
      {
        this.applySettings(response);
        this.setSuccess('Operations settings saved');
        this.finishSaving();
      },
      error: (error: unknown) =>
      {
        this.setHttpError(error);
        this.finishSaving();
      },
    });
  }

  public setContextRequiredError(message: string): void
  {
    this.invalidateSettings();
    this.setError(message);
  }

  public clearContextFeedback(): void
  {
    this.clearFeedback();
  }

  public onTimeZoneRegionChange(region: string): void
  {
    this.selectedTimeZoneRegion = region;
  }

  public onCancelPendingSlotDurationChange(checked: boolean): void {
    if (checked && this.initialSlotDurationMinutes !== null) {
      this.form.controls.slotDurationMinutes.setValue(this.initialSlotDurationMinutes);
    }
  }

  public onUseGlobalPublicAppBaseUrlChange(checked: boolean): void {
    this.form.controls.useGlobalPublicAppBaseUrl.setValue(checked, { emitEvent: false });
    this.form.controls.publicAppBaseUrl.setValue(
      (checked ? this.inheritedPublicAppBaseUrl : this.initialPublicAppBaseUrl) ?? '',
    );
    this.configurePublicAppBaseUrlValidation();
  }

  private publicAppBaseUrlPatch(): string | null | undefined {
    if (this.usesInheritedPublicAppBaseUrl) {
      return this.initialPublicAppBaseUrl === null ? undefined : null;
    }
    const value = this.form.controls.publicAppBaseUrl.value.trim();
    return value === (this.initialPublicAppBaseUrl ?? '') ? undefined : (value || null);
  }

  private configurePublicAppBaseUrlValidation(): void {
    this.form.controls.publicAppBaseUrl.setValidators(this.usesInheritedPublicAppBaseUrl
      ? [] : [publicAppUrlValidator, ...(this.loadedCompanyId ? [Validators.required, Validators.pattern(/\S/)] : [])]);
    this.form.controls.publicAppBaseUrl.updateValueAndValidity({ emitEvent: false });
  }

  public onTimeZoneValueChange(timeZone: string): void
  {
    this.form.controls.timeZone.setValue(timeZone);
    this.selectedTimeZoneRegion = findTimeZoneRegion(this.timeZoneCatalog, timeZone);
  }

  private applySettings(settings: CompanyOperationsSettingsDto): void
  {
    this.initialPublicAppBaseUrl = settings.publicAppBaseUrlOverride;
    this.inheritedPublicAppBaseUrl = settings.publicAppBaseUrlInherited;
    this.selectedTimeZoneRegion = findTimeZoneRegion(this.timeZoneCatalog, settings.timeZone);
    this.form.patchValue({
      useGlobalPublicAppBaseUrl: Boolean(this.loadedCompanyId) && settings.publicAppBaseUrlOverride === null,
      cancelPendingSlotDurationChange: false,
      slotDurationMinutes: settings.slotDurationMinutes,
      appointmentCompletionMode: settings.appointmentCompletionMode,
      generateDaysAhead: settings.generateDaysAhead,
      schedulerIntervalMinutes: settings.schedulerIntervalMinutes,
      schedulerEnabled: settings.schedulerEnabled,
      timeZone: settings.timeZone,
      clinicLabel: settings.clinicLabel,
      procedureTypeLabel: settings.procedureTypeLabel,
      publicAppBaseUrl: settings.publicAppBaseUrl ?? '',
    });
    this.initialSlotDurationMinutes = settings.slotDurationMinutes;
    this.sourceText = settings.source;
    this.publicAppBaseUrlSource = settings.publicAppBaseUrlSource;
    this.pendingSlotDurationMinutes = settings.pendingSlotDurationMinutes;
    this.pendingSlotDurationEffectiveFromDate = settings.slotDurationEffectiveFromDate;
    this.configurePublicAppBaseUrlValidation();
  }

  public ngOnDestroy(): void {
    this.disposeSettings();
  }
}
